import { Link } from 'react-router-dom';
import { ArrowRight, Clock3 } from 'lucide-react';
import { MantaRayIcon } from '@/components/icons/MantaRayIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getAllBlogPosts } from '../blog/posts';

const BLOG_POSTS = getAllBlogPosts();

function formatPublishedDate(value: string) {
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function BlogIndexPage() {
  return (
    <>
      <title>Mentara Blog | Online Tutoring, SAT Prep, and Family Guides</title>
      <meta
        name="description"
        content="Read the Mentara blog for practical advice on online tutoring, SAT prep, academic habits, and how families can support student progress."
      />
      <link rel="canonical" href="https://mentaratutors.com/blog/" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mentaratutors.com/blog/" />
      <meta property="og:title" content="Mentara Blog | Online Tutoring, SAT Prep, and Family Guides" />
      <meta
        property="og:description"
        content="Practical articles for students, parents, and tutors from the Mentara team."
      />
      <meta property="og:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content="https://mentaratutors.com/blog/" />
      <meta property="twitter:title" content="Mentara Blog | Online Tutoring, SAT Prep, and Family Guides" />
      <meta
        property="twitter:description"
        content="Practical articles for students, parents, and tutors from the Mentara team."
      />
      <meta property="twitter:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />

      <div className="bg-background">
        <section className="py-20 md:py-28 px-4">
          <div className="container max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <MantaRayIcon className="h-16 w-16 animate-swim" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight md:text-5xl leading-tight">
              Practical advice for families, students, and tutors.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              A simple library of clear, useful guidance on online tutoring, SAT prep, academic
              habits, and how to build steady learning momentum.
            </p>
          </div>
        </section>

        <section className="py-16 px-4 border-t border-border/40">
          <div className="container max-w-3xl mx-auto">
            {BLOG_POSTS.length > 0 ? (
              <div className="grid gap-6">
                {BLOG_POSTS.map((post) => (
                  <Card key={post.slug} className="border bg-muted/20 hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span>{formatPublishedDate(post.publishedAt)}</span>
                        <span className="inline-flex items-center gap-1">
                          <Clock3 className="h-4 w-4" />
                          {post.readTime}
                        </span>
                      </div>
                      <h2 className="text-2xl font-semibold tracking-tight">
                        <Link to={`/blog/${post.slug}`} className="hover:text-primary transition-colors">
                          {post.title}
                        </Link>
                      </h2>
                      <p className="mt-3 text-muted-foreground leading-relaxed">{post.description}</p>
                      <div className="mt-6">
                        <Button variant="outline" asChild>
                          <Link to={`/blog/${post.slug}`}>
                            Read article
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border bg-muted/20">
                <CardContent className="p-8 text-center">
                  <h2 className="text-2xl font-semibold tracking-tight">New articles are on the way.</h2>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    We&apos;re getting the blog ready. Check back soon for practical guides on tutoring,
                    SAT prep, and supporting student progress.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        <section className="py-20 px-4 border-t border-border/40">
          <div className="container max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">Looking for tutoring support now?</h2>
            <p className="text-lg text-muted-foreground">
              Explore Mentara to find personalized online tutoring, SAT prep, and tools that keep
              families in the loop.
            </p>
            <Button size="lg" asChild>
              <Link to="/register">
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
}
